import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useRoleContext } from '../hooks/useRoleContext';
import { resolveFooterContext } from '../lib/footerNavigation';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterSubscribe({ variant = 'default', source = 'website' }) {
  const { t } = useLanguage();
  const { user, isAdmin } = useAuth();
  const { viewAsRole } = useRoleContext();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('newsletter.error_email', 'Please enter a valid email address'));
      return;
    }

    setLoading(true);

    const roleContext = resolveFooterContext({ user, isAdmin, viewAsRole });

    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source,
          role_context: roleContext,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail = data.detail || data.message || 'Subscription failed';
        throw new Error(typeof detail === 'string' ? detail : 'Subscription failed');
      }

      setSubscribed(true);
      setEmail('');
      if (data.already_subscribed) {
        toast.success(t('newsletter.already_subscribed', 'This email is already subscribed'));
      } else {
        toast.success(t('newsletter.success', 'Successfully subscribed!'));
      }
    } catch (err) {
      const message = err.message || '';
      if (message.toLowerCase().includes('already subscribed')) {
        setSubscribed(true);
        setError(t('newsletter.already_subscribed', 'This email is already subscribed'));
        toast.success(t('newsletter.already_subscribed', 'This email is already subscribed'));
      } else {
        setError(t('newsletter.error', 'Failed to subscribe. Please try again.'));
        toast.error(t('newsletter.error', 'Failed to subscribe. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className={`flex items-center gap-2 ${variant === 'compact' ? 'text-sm' : ''}`}>
        <CheckCircle className="w-5 h-5 text-emerald-500" />
        <span className="text-emerald-600">
          {t('newsletter.subscribed', 'Thanks for subscribing!')}
        </span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            placeholder={t('newsletter.placeholder', 'Enter your email')}
            className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 text-sm h-9"
            data-testid="newsletter-email-compact"
          />
          <Button
            type="submit"
            disabled={loading}
            size="sm"
            className="bg-military-gold hover:bg-military-gold/90 text-slate-950 h-9 px-3"
            data-testid="newsletter-submit-compact"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          </Button>
        </div>
        {error && (
          <p className="text-red-400 text-xs flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        )}
      </form>
    );
  }

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-military-gold/20 rounded-full flex items-center justify-center">
          <Mail className="w-5 h-5 text-military-gold" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-slate-800">
            {t('newsletter.title', 'Stay Updated')}
          </h3>
          <p className="text-slate-600 text-sm">
            {t('newsletter.subtitle', 'Get the latest properties and PCS tips')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          placeholder={t('newsletter.placeholder', 'Enter your email address')}
          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
          data-testid="newsletter-email"
        />
        {error && (
          <p className="text-red-400 text-sm flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-military-gold hover:bg-military-gold/90 text-slate-950"
          data-testid="newsletter-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('newsletter.subscribing', 'Subscribing...')}
            </>
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              {t('newsletter.subscribe', 'Subscribe')}
            </>
          )}
        </Button>
        <p className="text-slate-800 text-xs text-center">
          {t('newsletter.privacy_note', 'We respect your privacy. Unsubscribe anytime.')}
        </p>
      </form>
    </div>
  );
}
